// js/shared/marketing-i18n.js — the public marketing page, in both languages
// the practice speaks, and in the order the reader arrives in.
//
// QUEUE ITEM BS. index.html used to open with developmental disabilities and
// SDP: the hero subline, the stat chips, service 01 and two whole sections. A
// private-pay adult in San Diego looking for a trainer after a hip replacement
// read that page and concluded it was not for them. That is a marketing problem
// and a COMPLIANCE one: Title 17 §57210 keeps the trainer's usual-and-customary
// rate only while at least 30% of clients are non-Regional-Center private pay.
// The specialty is the proof, not the door — so the page leads with the reader
// who finds us on their own, and the adaptive depth sits one section down where
// it reads as evidence rather than as a filter.
//
// THE SHAPE, deliberately identical to js/shared/forms/trainer-apply-i18n.js
// (queue item BC) and js/shared/forms/intake-i18n.js (queue item AK): two flat
// maps with IDENTICAL key sets, applied onto the document through `data-i18n` /
// `data-i18n-placeholder` / `data-i18n-aria-label` / `data-i18n-title`. Nothing
// visible in index.html is a literal, which is what lets META-12 compare the two
// packs on every PR — an English sentence added without its Spanish counterpart
// fails the build rather than shipping an English paragraph into a Spanish page.
//
// USTED. Every string addresses the reader formally: a stranger reading about a
// service, a rate and a contract. Neutralized Mexican Spanish, SDRC San Diego
// baseline, no regional slang (CLAUDE.md's variant rule, META-49's register).
//
// A LANGUAGE CHANGES WHAT THE READER READS AND NOTHING ABOUT WHAT IS STORED.
// The contact form's service <select> keeps a stable English `value=` in both
// languages, so `leads.service` holds one vocabulary; only the LABEL moves.
//
// SCOPE-OF-PRACTICE (SCOPE_LANGUAGE_GUARDRAILS_2026_06_16.md). James Kennedy is
// a certified personal TRAINER, not a licensed healthcare provider. Nothing here
// says rehab, therapy, treatment, patient or prescribe: it says conditioning,
// training, client, and "after your physical therapy ends". The disclaimer under
// Services is part of the page, not a footnote.
//
// PURE. No DOM at module scope, no fetch, no cross-realm state — the contract
// the other three packs keep, which is what lets node compare them without a
// browser.

import { INQUIRY_REPLY } from './reply-promise.js';

// ── English ──────────────────────────────────────────────────────────────
//
// QUEUE ITEM KV — the home page as JK approved it on Sep 30 and Oct 1
// (kaf-mockups-v9/pages/public-home.mjs). Every line is JK's own wording from
// the site he had, lightly polished (long sentences split, comma splices fixed,
// no em dashes, no prices, plain words for jargon). No price appears anywhere:
// rates are for the free call (CI-META-3t holds it).
const EN = {
  'doc.title': 'KinetiqAF · In-home personal training in San Diego',
  'doc.description': 'A personal trainer who comes to your home anywhere in San Diego County: after surgery or injury, strength at any age, and adaptive fitness for families. Ask for a free 20-minute call.',

  // Header, drawer and skip link
  'skip': 'Skip to content',
  'nav.services': 'Services',
  'nav.where': 'Where I train',
  'nav.adaptive': 'Adaptive fitness',
  'nav.funding': 'Funding',
  'nav.about': 'About',
  'nav.contact': 'Contact',
  'nav.login': 'Client login',
  'nav.loginSub': 'For current clients',
  'nav.free': 'Free call',
  'nav.drawerCta': 'Ask for a free call',
  'nav.menu': 'Menu',
  'nav.closeMenu': 'Close menu',
  'nav.lang': 'Español',
  'nav.langSub': 'Ver esta página en español',
  'aria.main': 'Main',
  'aria.footer': 'Footer',

  // Hero (version D, Oct 1)
  'hero.eyebrow': 'In-home personal training · San Diego',
  'hero.h1a': 'Move better. Live stronger.',
  'hero.h1b': 'For every body.',
  'hero.sub': 'A trainer who comes to your home. After surgery, for strength at any age, or adaptive fitness.',
  'hero.ctaCall': 'Call for a free 20-minute chat',
  'hero.ctaForm': 'Or leave your number and I will call you',
  'hero.ctaPrimary': 'Ask for a free 20-minute call',
  'hero.ctaPhone': 'Call (442) 375-5090',
  'hero.lang': 'Calls and sessions are in English; the forms are in Spanish too.',
  'proof.aria': 'Why people choose KinetiqAF',
  'proof.nasmNum': 'NASM',
  'proof.nasmLabel': 'Certified personal trainer',
  'proof.nasmTitle': 'National Academy of Sports Medicine',
  'proof.specialNum': 'Special Strong',
  'proof.specialLabel': 'Certified adaptive trainer',
  'proof.yearsNum': '10+',
  'proof.yearsLabel': 'Years coaching',
  'proof.fundingNum': 'Regional Center',
  'proof.fundingLabel': 'and SDP funding accepted',
  'proof.fundingTitle': 'Regional Center and Self-Determination Program (SDP) funding accepted',
  'proof.servedLabel': 'Clients served',
  'proof.asOf': 'as of {date}',
  'proof.googleNum': 'Google',
  'proof.googleLabel': 'Read my reviews',
  'photo.heroAlt': 'James Kennedy coaching a client at home',
  'photo.surgeryAlt': 'A client doing a supported squat at home',
  'photo.strengthAlt': 'An adult client lifting weights in a home gym',
  'photo.adaptiveAlt': 'An adaptive fitness session in a park',
  'photo.meetAlt': 'James Kennedy',

  // Meet your trainer (only with a consented portrait)
  'meet.label': 'Meet your trainer',
  'meet.h2': 'Hi, I’m James.',
  'meet.body': 'I have coached for more than 10 years: athletes, medical fitness and personal training. Call or text me directly.',
  'meet.text': 'Text me',
  'meet.call': 'Call',

  // Services: three cards
  'who.label': 'What I offer',
  'who.h2': 'Three ways to train with me',
  'who.sub': 'Same coach, same care. Pick the one that sounds like you.',
  'who.cta': 'Talk to me about this',
  'who.surgery.for': 'After surgery or injury',
  'who.surgery.title': 'We pick up when physical therapy ends',
  'who.surgery.lede': 'A new hip or knee, a repair, an old injury.',
  'who.surgery.l1': 'Your doctor clears you first. Then we start slow and build.',
  'who.surgery.l2': 'Work on the aches and stiff spots that keep coming back.',
  'who.strength.for': 'Stronger at any age',
  'who.strength.title': 'Strength, balance and ease of movement',
  'who.strength.lede': 'From any starting point.',
  'who.strength.l1': 'Food coaching that fits your week. It is not a diet.',
  'who.strength.l2': 'Train on your own, or with a partner.',
  'who.adaptive.for': 'Adaptive fitness for your family',
  'who.adaptive.title': 'Calm sessions with a set routine',
  'who.adaptive.lede': 'For autism, cerebral palsy, Down syndrome and other disabilities.',
  'who.adaptive.l1': 'Planned for how your child or adult learns.',
  'who.adaptive.l2': 'Regional Center and Self-Determination Program (SDP) funds can pay. Regional Center sessions take place in community settings like parks and pools.',
  'services.disclaimer': 'James Kennedy is a certified personal trainer, not a licensed healthcare provider. KinetiqAF is fitness training and does not diagnose, treat, or rehabilitate any medical condition. It complements, and does not replace, care from your physician, physical therapist, or other licensed provider.',

  // Where I train (new, Sep 30)
  'where.label': 'Where I train',
  'where.h2': 'A gym is never the barrier',
  'where.sub': 'I work with clients in their homes, parks and community spaces across San Diego.',
  'where.home.t': 'At home',
  'where.home.s': 'Most sessions are in-home.',
  'where.building.t': 'Your building’s gym',
  'where.building.s': 'Right where you live.',
  'where.park.t': 'A park, rec center or pool',
  'where.park.s': 'Community settings near you. Regional Center sessions take place here.',
  'where.gym.t': 'A full gym',
  'where.gym.s': 'For clients who prefer a full gym, I work out of a facility in Linda Vista.',
  'where.online.t': 'Online',
  'where.online.s': 'Virtual sessions for check-ins on your program and nutrition coaching.',
  'where.areaLabel': 'All of San Diego County:',
  'where.area': 'central San Diego, the coast, North County, East County and South Bay.',

  // Adaptive fitness
  'adaptive.label': 'Adaptive fitness',
  'adaptive.h2': 'Training planned around your family member',
  'adaptive.sub': 'I hold the Special Strong adaptive trainer certification. Sessions are calm, structured and built around how each person learns.',
  'adaptive.t1.title': 'Autism',
  'adaptive.t1.desc': 'A set routine, sensory-aware sessions, and a way of talking that fits the person.',
  'adaptive.t2.title': 'Cerebral palsy',
  'adaptive.t2.desc': 'Movement, muscle tone and getting around, whether or not the person walks.',
  'adaptive.t3.title': 'Down syndrome',
  'adaptive.t3.desc': 'Strength, balance and heart health, at a pace that fits.',
  'adaptive.t4.title': 'Intellectual and developmental disabilities',
  'adaptive.t4.desc': 'Plans built around what the person can do, what they want, and the support they need.',
  'adaptive.t5.title': 'Sensory and behavior needs',
  'adaptive.t5.desc': 'Sessions that help with calm and self-control through movement.',
  // Safety and trust (decision d26 a): each row shows only with JK's own fact
  // from Settings › Business › On your website; the words below are the labels.
  'safety.label': 'Safety and trust',
  'safety.cpr': 'CPR and first aid',
  'safety.cprTitle': 'Cardiopulmonary resuscitation (CPR) and first aid',
  'safety.insurance': 'Insurance',
  'safety.background': 'Background check',
  'safety.ages': 'Ages served',
  'safety.watch': 'You’re welcome to stay and watch',

  // How funding works — queue items IN / R224, GV, then KV. Factual: "vendor",
  // never endorsement; regional-center sessions are in community settings only
  // (the approved scope). The vendor number is on no public page (queue item
  // KVb, JK Oct 6): James sends his vendor details to the coordinator himself.
  'rc.label': 'How funding works',
  'rc.h2': 'Regional Center and Self-Determination families',
  'rc.vendor': 'KinetiqAF is a vendor with the San Diego Regional Center.',
  'rc.where': 'Regional Center sessions take place in community settings like parks, recreation centers and pools.',
  'rc.stepsAria': 'How regional-center funding starts',
  'rc.s1.title': 'Ask your service coordinator',
  'rc.s1.body': 'Ask them to add KinetiqAF to your plan. I send them my vendor details. If you do not know who that is, call the regional center and ask.',
  'rc.s2.title': 'Written authorization',
  'rc.s2.body': 'Your regional center authorizes the sessions in writing.',
  'rc.s3.title': 'Your first session',
  'rc.s3.body': 'We schedule your first session.',
  'rc.handlesTitle': 'What I handle',
  'rc.handles': 'The vendor paperwork, the billing, and the session notes your coordinator asks for.',
  'rc.sdpLead': 'On the Self-Determination Program?',
  'rc.sdp': 'I also work through your Financial Management Service (FMS). Covered sessions cost your family nothing.',
  'rc.ask': 'Ask me about funding',

  // About
  'about.label': 'About James Kennedy',
  'about.h2a': 'Training built on',
  'about.h2b': 'corrective exercise',
  'about.h2c': 'and',
  'about.h2d': 'real-world experience',
  'about.p1': 'I have coached for more than 10 years: athletes, medical fitness and personal training. Whether you are coming back from surgery, living with a disability, or just want to move and feel better, the plan is built for you.',
  'about.p2': 'Before I got certified, I worked as a physical-therapy aide. That is where the post-surgical side of my practice comes from. I know what the last weeks of physical therapy look like, and I know what it takes to pick somebody up from there and keep building.',
  'about.credsIntro': 'My training and certifications:',
  'about.cred1.name': 'NASM-CPT',
  'about.cred1.desc': 'Certified Personal Trainer',
  'about.cred2.name': 'NASM-CES',
  'about.cred2.desc': 'Corrective Exercise Specialist',
  'about.cred3.name': 'NASM-CNC',
  'about.cred3.desc': 'Certified Nutrition Coach',
  'about.cred4.name': 'Special Strong',
  'about.cred4.desc': 'Certified Adaptive Trainer',
  'about.cred5.name': 'B.S. Kinesiology',
  'about.cred5.desc': 'Cal State San Marcos',
  'about.quote': '“Movement is not a privilege. It is a right, and my job is to make it accessible for every individual, regardless of ability.”',
  'about.attr': 'James Kennedy, KinetiqAF',

  // How it works
  'process.label': 'How it works',
  'process.h2': 'From first call to first session',
  'process.s1.title': 'A free 20-minute call',
  'process.s1.body': 'We talk about your goals and your history. After the call I send you an intake form. It takes about 15 minutes and saves as you go.',
  'process.s2.title': 'Assessment',
  'process.s2.body': 'I come to you and see how you move, so we start in the right place.',
  'process.s3.title': 'Your plan',
  'process.s3.body': 'A program built for your goals, your space and your schedule.',
  'process.s4.title': 'Training',
  'process.s4.body': 'Sessions at your home or nearby. The plan grows as you do.',

  // What families ask me first — queue items CY, then KV (E-09). The trainer's
  // own words, attributed to nobody; published quotes replace them (GV).
  'stories.label': 'Common worries',
  'stories.h2': 'What families ask me first',
  'stories.s1.title': 'Families who have tried other trainers',
  'stories.s1.body': 'Most adaptive families who come to me have tried someone else first. The gap is rarely fitness knowledge. It is knowing how to change how you communicate, how to structure a session, and how to read a day that is not going well. That is the part I trained for.',
  'stories.s2.title': 'Starting again after surgery',
  'stories.s2.body': 'The thing I hear most after a joint replacement or a repair is nervousness about starting at all. So we start with an assessment, go slower than most people expect, and build around the life you are returning to, not a template.',
  'stories.quotesLabel': 'In their words',
  'stories.quotesH2': 'What clients say',
  'stories.s3.title': 'The gym was the barrier',
  'stories.s3.body': 'People tell me the obstacle was never motivation. It was the drive, the crowd and the equipment. Training at home or in your neighborhood removes most of that before we begin.',

  // Questions (FAQ_QUESTIONS / FAQ_ANSWERS in the mockup, word for word)
  'faq.label': 'Common questions',
  'faq.h2': 'Before you reach out',
  'faq.q1': 'Do I need a doctor’s clearance after surgery?',
  'faq.a1': 'Usually yes, and it is worth getting. If you are coming off a joint replacement, a repair or any procedure with activity restrictions, I ask for written clearance from your surgeon or physician before we start loading anything. It costs you one message to their office, and it tells me exactly which movements are open and which are not. I am a certified personal trainer, not a licensed healthcare provider. Clearance is what keeps the training on the right side of that line.',
  'faq.q2': 'What does it cost?',
  'faq.a2': 'We discuss rates and packages at your free 20-minute call, once we know how often you want to train. There is no pressure to buy a package. Sessions funded by the Regional Center or SDP have no out-of-pocket cost for covered services. Everything is set out in a simple written agreement you can cancel at any time.',
  'faq.q3': 'Do you come to my home or my gym?',
  'faq.a3': 'Whichever works better for you. Most sessions are in-home or community-based throughout San Diego County: your living room, your building’s gym, a local park. For clients who prefer a full gym setting, I work out of a facility in Linda Vista. Virtual sessions are available for check-ins on your program and nutrition coaching. Sessions paid by the Regional Center happen in community places like parks and pools, not at home.',
  'faq.q4': 'Does my family member qualify for SDP funding?',
  'faq.a4': 'The Self-Determination Program is open to Regional Center clients in California. If your family member gets services from a Regional Center (such as San Diego Regional Center), they may be able to use SDP funding to hire providers they choose, including adaptive fitness trainers like KinetiqAF. Contact me and I will walk you through it at no charge.',
  'faq.q5': 'Which FMS providers do you work with?',
  'faq.a5': 'KinetiqAF works with Financial Management Service providers across the San Diego network. If you already have one, tell me who. I will confirm we are set up with them, or complete any paperwork needed on my end. You request KinetiqAF through your FMS, and I handle the rest.',
  'faq.q6': 'Have you worked with people with intellectual or developmental disabilities?',
  'faq.a6': 'I hold the Special Strong Certified Adaptive Trainer designation, one of the few credentials built for fitness professionals who work with people with intellectual and developmental disabilities. My approach uses Applied Behavior Analysis (ABA) principles, sensory regulation strategies and adaptive program design. I do not use a one-size-fits-all protocol: every client has their own plan.',
  'faq.q7': 'How long before I see results?',
  'faq.a7': 'This depends heavily on your starting point and goals. For post-surgical clients, meaningful functional improvements typically appear within 6 to 8 weeks of consistent training. For adaptive clients, behavioral and motor skill gains often appear earlier, while physical fitness metrics take longer. I set realistic expectations during the free call and track measurable benchmarks from the first session.',
  'faq.q8': 'What is the cancellation policy?',
  'faq.a8': '24-hour notice is required for cancellations. Late cancellations (under 24 hours) and no-shows are charged the session fee. I understand emergencies happen. I will always work with you on a case-by-case basis, especially for adaptive clients, where unexpected regulation challenges are part of the territory.',
  'faq.more': 'Have a question that is not here?',
  'faq.cta': 'Ask for a free call',

  // Contact
  'contact.label': 'Get in touch',
  'contact.h2': 'Ask for a free 20-minute call',
  'contact.body': 'Leave your name and phone number.',
  'contact.phone': '(442) 375-5090',
  'contact.phoneSub': 'Call or text',
  'contact.email': 'james@kinetiqaf.com',
  'contact.emailSub': 'Email',
  'contact.areaTitle': 'Where I train',
  'contact.area': 'All of San Diego County: central San Diego, the coast, North County, East County and South Bay.',
  'form.trap': 'Leave this field blank',
  'form.name': 'Your name',
  'form.phone': 'Phone number',
  'form.phoneHelp': 'So I can call you back',
  'form.email': 'Email',
  'form.emailHelp': 'So I can write back',
  'form.useEmail': 'Use email instead',
  'form.usePhone': 'Use a phone number instead',
  'form.service': 'What brings you',
  'form.opt.none': 'Choose one',
  'form.opt.surgery': 'After surgery or injury',
  'form.opt.strength': 'Getting stronger at any age',
  'form.opt.adaptive': 'Adaptive fitness for a family member',
  'form.opt.exploring': 'Not sure yet',
  'form.errName': 'Enter your name',
  'form.errPhone': 'Enter a phone number, like 619 555 0123',
  'form.errEmail': 'Enter an email address, like name@example.com',
  'form.errService': 'Choose what brings you',
  'form.errSend': 'That did not send. Your answers are still here. Try again, or call me.',
  'form.call': 'Call (442) 375-5090',
  'form.retry': 'Try again',
  'form.submit': 'Ask for my free call',
  'form.sending': 'Sending…',
  'form.sent': 'Sent',
  'form.gotIt': 'Got it, {name}.',
  'form.callBy': 'I will call you by the end of {day}.',
  'form.writeBy': 'I will write back by the end of {day}.',
  'form.miss': 'If I miss you, I leave a message and send a text.',

  // The phone call bar
  'bar.aria': 'Contact James',
  'bar.call': 'Call',
  'bar.ask': 'Ask for a free call',

  // Footer
  'footer.copy': '© 2026 James Kennedy & Associates LLC · San Diego, CA',
  'footer.privacy': 'Privacy policy',
  'footer.terms': 'Terms of service',
  'promise.reply': INQUIRY_REPLY.en,

  // ── The contact card (queue item IE) ──────────────────────────────────
  //
  // `/card` is the page behind the QR code on the printed business card, and
  // its words are the CARD's words: "NASM CPT · CES · CNC · Special Strong
  // Certified" is set in type on the thing the reader is holding when they
  // scan it, so the page says the same. Queue item KVb (JK approved the v9
  // card mockup, kaf-mockups-v9/pages/public-card.mjs; audit E-35, E-36):
  // the role line names the business ("Personal trainer · Founder of
  // KinetiqAF"), the enquiry row is the free call the site offers (it opens
  // the home page's contact form, not the intake), and the service area is
  // a row of its own. There is no Directions row: no public address.
  // That is why the role and the credentials are strings here rather than
  // `trainers.title` / `trainers.credentials` read out of the database: the
  // database rows are the ADMIN's words for the same person ("Owner & Lead
  // Trainer"), and a card page that disagreed with the card would read as the
  // wrong James.
  //
  // The page's own `doc.title` and `doc.description` are NOT the two keys at
  // the top of this pack — see the `data-i18n-title-key` note in
  // applyMarketingLang. One pack, one painter, two documents.
  'card.doc.title': 'James Kennedy · KinetiqAF · Save my contact',
  'card.doc.description': 'Save James Kennedy’s contact details, call or text him, or ask for a free 20-minute call. In-home personal training in San Diego.',
  // The share line a link preview shows under "James Kennedy · KinetiqAF"
  // (twitter:description; og:description is the role and the tagline).
  'card.shareLine': 'Save my contact, call or text me, or ask for a free call.',
  'card.role': 'Personal trainer · Founder of KinetiqAF',
  'card.creds': 'NASM CPT · CES · CNC · Special Strong Certified',
  'card.what': 'In-home personal training in San Diego: after surgery, strength at any age, adaptive fitness.',
  'card.save': 'Save contact',
  'card.saveHint': 'Adds James to your contacts.',
  'card.waysLabel': 'Ways to reach James',
  'card.row.call': 'Call',
  'card.row.text': 'Text',
  'card.row.email': 'Email',
  'card.row.ask': 'Ask for a free call',
  'card.row.askSub': '20 minutes. James calls you back.',
  'card.row.website': 'Website',
  'card.row.review': 'Leave a Google review',
  'card.row.appIos': 'Get the app for iPhone',
  'card.row.appAndroid': 'Get the app for Android',
  'card.area': 'Service area',
  'card.areaSub': 'All of San Diego County · James comes to you',
  'card.markAlt': 'KinetiqAF',
  'card.footAria': 'More',
  'card.lang': 'Español',
  'card.langAria': 'Cambiar a español',
};

// ── Español (neutralized Mexican Spanish, usted) ─────────────────────────
const ES = {
  'doc.title': 'KinetiqAF · Entrenamiento personal a domicilio en San Diego',
  'doc.description': 'Un entrenador personal que va a su casa en todo el condado de San Diego: después de una cirugía o lesión, fuerza a cualquier edad y acondicionamiento adaptado para familias. Pida una llamada gratuita de 20 minutos.',

  // Encabezado, menú y enlace para saltar
  'skip': 'Saltar al contenido',
  'nav.services': 'Servicios',
  'nav.where': 'Dónde entreno',
  'nav.adaptive': 'Acondicionamiento adaptado',
  'nav.funding': 'Financiamiento',
  'nav.about': 'Acerca de',
  'nav.contact': 'Contacto',
  'nav.login': 'Acceso de clientes',
  'nav.loginSub': 'Para clientes actuales',
  'nav.free': 'Llamada gratuita',
  'nav.drawerCta': 'Pida una llamada gratuita',
  'nav.menu': 'Menú',
  'nav.closeMenu': 'Cerrar menú',
  'nav.lang': 'English',
  'nav.langSub': 'View this page in English',
  'aria.main': 'Principal',
  'aria.footer': 'Pie de página',

  // Portada
  'hero.eyebrow': 'Entrenamiento personal a domicilio · San Diego',
  'hero.h1a': 'Muévase mejor. Viva con más fuerza.',
  'hero.h1b': 'Para todo cuerpo.',
  'hero.sub': 'Un entrenador que va a su casa. Después de una cirugía, para ganar fuerza a cualquier edad o acondicionamiento adaptado.',
  'hero.ctaCall': 'Llamar: charla gratis de 20 min',
  'hero.ctaForm': 'O deje su número y yo le llamo',
  'hero.ctaPrimary': 'Pida una llamada gratuita de 20 minutos',
  'hero.ctaPhone': 'Llamar al (442) 375-5090',
  'hero.lang': 'Las llamadas y sesiones son en inglés. Puede traer a alguien que le ayude. Los formularios están en español.',
  'proof.aria': 'Por qué eligen KinetiqAF',
  'proof.nasmNum': 'NASM',
  'proof.nasmLabel': 'Entrenador personal certificado',
  'proof.nasmTitle': 'National Academy of Sports Medicine',
  'proof.specialNum': 'Special Strong',
  'proof.specialLabel': 'Entrenador adaptado certificado',
  'proof.yearsNum': '10+',
  'proof.yearsLabel': 'Años de entrenamiento',
  'proof.fundingNum': 'Centro Regional',
  'proof.fundingLabel': 'y se acepta financiamiento del SDP',
  'proof.fundingTitle': 'Se acepta financiamiento del Centro Regional y del Programa de Autodeterminación (SDP)',
  'proof.servedLabel': 'Clientes atendidos',
  'proof.asOf': 'al {date}',
  'proof.googleNum': 'Google',
  'proof.googleLabel': 'Lea mis reseñas',
  'photo.heroAlt': 'James Kennedy entrenando a un cliente en su casa',
  'photo.surgeryAlt': 'Un cliente haciendo una sentadilla con apoyo en casa',
  'photo.strengthAlt': 'Un cliente adulto levantando pesas en un gimnasio en casa',
  'photo.adaptiveAlt': 'Una sesión de acondicionamiento adaptado en un parque',
  'photo.meetAlt': 'James Kennedy',

  // Conozca a su entrenador
  'meet.label': 'Conozca a su entrenador',
  'meet.h2': 'Hola, soy James.',
  'meet.body': 'Tengo más de 10 años como entrenador: atletas, acondicionamiento médico y entrenamiento personal. Llámeme o envíeme un mensaje directamente.',
  'meet.text': 'Envíeme un mensaje',
  'meet.call': 'Llamar',

  // Servicios: tres tarjetas
  'who.label': 'Lo que ofrezco',
  'who.h2': 'Tres maneras de entrenar conmigo',
  'who.sub': 'El mismo entrenador, el mismo cuidado. Elija la que más se parezca a usted.',
  'who.cta': 'Hablemos de esto',
  'who.surgery.for': 'Después de una cirugía o lesión',
  'who.surgery.title': 'Seguimos donde termina la terapia física',
  'who.surgery.lede': 'Una cadera o rodilla nueva, una reparación, una lesión antigua.',
  'who.surgery.l1': 'Primero su médico le da el visto bueno. Luego empezamos despacio y avanzamos.',
  'who.surgery.l2': 'Trabajamos los dolores y las zonas rígidas que siempre regresan.',
  'who.strength.for': 'Más fuerte a cualquier edad',
  'who.strength.title': 'Fuerza, equilibrio y facilidad de movimiento',
  'who.strength.lede': 'Desde cualquier punto de partida.',
  'who.strength.l1': 'Orientación de alimentación que se ajusta a su semana. No es una dieta.',
  'who.strength.l2': 'Entrene solo o con otra persona.',
  'who.adaptive.for': 'Acondicionamiento adaptado para su familia',
  'who.adaptive.title': 'Sesiones tranquilas con una rutina fija',
  'who.adaptive.lede': 'Para autismo, parálisis cerebral, síndrome de Down y otras discapacidades.',
  'who.adaptive.l1': 'Pensadas en cómo aprende su hijo o familiar adulto.',
  'who.adaptive.l2': 'Los fondos del Centro Regional y del Programa de Autodeterminación (SDP) pueden pagarlas. Las sesiones del Centro Regional se realizan en espacios comunitarios, como parques y albercas.',
  'services.disclaimer': 'James Kennedy es entrenador personal certificado, no un proveedor de salud con licencia. KinetiqAF es entrenamiento físico y no diagnostica, trata ni rehabilita ninguna condición médica. Complementa, y no reemplaza, la atención de su médico, su terapeuta físico u otro proveedor con licencia.',

  // Dónde entreno
  'where.label': 'Dónde entreno',
  'where.h2': 'Un gimnasio nunca es el obstáculo',
  'where.sub': 'Trabajo con mis clientes en sus casas, en parques y en espacios comunitarios de todo San Diego.',
  'where.home.t': 'En su casa',
  'where.home.s': 'La mayoría de las sesiones son a domicilio.',
  'where.building.t': 'El gimnasio de su edificio',
  'where.building.s': 'Justo donde vive.',
  'where.park.t': 'Un parque, centro recreativo o alberca',
  'where.park.s': 'Espacios comunitarios cerca de usted. Aquí se realizan las sesiones del Centro Regional.',
  'where.gym.t': 'Un gimnasio completo',
  'where.gym.s': 'Para quienes prefieren un gimnasio completo, trabajo en una instalación en Linda Vista.',
  'where.online.t': 'En línea',
  'where.online.s': 'Sesiones virtuales para revisar su programa y para el coaching de nutrición.',
  'where.areaLabel': 'Todo el condado de San Diego:',
  'where.area': 'el centro de San Diego, la costa, North County, East County y South Bay.',

  // Acondicionamiento adaptado
  'adaptive.label': 'Acondicionamiento adaptado',
  'adaptive.h2': 'Entrenamiento planeado alrededor de su familiar',
  'adaptive.sub': 'Tengo la certificación Special Strong de entrenador adaptado. Las sesiones son tranquilas, estructuradas y se basan en cómo aprende cada persona.',
  'adaptive.t1.title': 'Autismo',
  'adaptive.t1.desc': 'Una rutina fija, sesiones con cuidado sensorial y una manera de hablar que se ajusta a la persona.',
  'adaptive.t2.title': 'Parálisis cerebral',
  'adaptive.t2.desc': 'Movimiento, tono muscular y desplazamiento, camine o no la persona.',
  'adaptive.t3.title': 'Síndrome de Down',
  'adaptive.t3.desc': 'Fuerza, equilibrio y salud del corazón, a un ritmo que le quede bien.',
  'adaptive.t4.title': 'Discapacidad intelectual y del desarrollo',
  'adaptive.t4.desc': 'Planes basados en lo que la persona puede hacer, lo que quiere y el apoyo que necesita.',
  'adaptive.t5.title': 'Necesidades sensoriales y de conducta',
  'adaptive.t5.desc': 'Sesiones que ayudan con la calma y el autocontrol a través del movimiento.',
  'safety.label': 'Seguridad y confianza',
  'safety.cpr': 'RCP y primeros auxilios',
  'safety.cprTitle': 'Reanimación cardiopulmonar (RCP) y primeros auxilios',
  'safety.insurance': 'Seguro',
  'safety.background': 'Revisión de antecedentes',
  'safety.ages': 'Edades que atiendo',
  'safety.watch': 'Puede quedarse a ver la sesión',

  // Cómo funciona el financiamiento — queue items IN / R224, GV y KV.
  // "Proveedor", nunca respaldo; sesiones financiadas por el centro regional
  // solo en espacios comunitarios; el número de proveedor solo en esta sección.
  'rc.label': 'Cómo funciona el financiamiento',
  'rc.h2': 'Familias del Centro Regional y del Programa de Autodeterminación',
  'rc.vendor': 'KinetiqAF es proveedor (vendor) del San Diego Regional Center.',
  'rc.where': 'Las sesiones del Centro Regional se realizan en espacios comunitarios, como parques, centros recreativos y albercas.',
  'rc.stepsAria': 'Cómo empieza el financiamiento del centro regional',
  'rc.s1.title': 'Hable con su coordinador de servicios',
  'rc.s1.body': 'Pídale que agregue a KinetiqAF a su plan. Yo le envío mis datos de proveedor a su coordinador. Si no sabe quién es, llame al centro regional y pregunte.',
  'rc.s2.title': 'Autorización por escrito',
  'rc.s2.body': 'Su centro regional autoriza las sesiones por escrito.',
  'rc.s3.title': 'Su primera sesión',
  'rc.s3.body': 'Programamos su primera sesión.',
  'rc.handlesTitle': 'De qué me encargo yo',
  'rc.handles': 'Del papeleo de proveedor, de la facturación y de las notas de sesión que pida su coordinador.',
  'rc.sdpLead': '¿Está en el Programa de Autodeterminación?',
  'rc.sdp': 'También trabajo a través de su proveedor de Servicios de Administración Financiera (FMS). Las sesiones cubiertas no le cuestan nada a su familia.',
  'rc.ask': 'Pregúnteme sobre el financiamiento',

  // Acerca de
  'about.label': 'Acerca de James Kennedy',
  'about.h2a': 'Un entrenamiento basado en',
  'about.h2b': 'ejercicio correctivo',
  'about.h2c': 'y',
  'about.h2d': 'experiencia real',
  'about.p1': 'Tengo más de 10 años como entrenador: atletas, acondicionamiento médico y entrenamiento personal. Ya sea que esté regresando después de una cirugía, que viva con una discapacidad o que simplemente quiera moverse y sentirse mejor, el plan se hace para usted.',
  'about.p2': 'Antes de certificarme trabajé como asistente de terapia física. De ahí viene el lado posquirúrgico de mi práctica. Sé cómo se ven las últimas semanas de terapia física, y sé lo que hace falta para retomar el trabajo desde ahí y seguir construyendo.',
  'about.credsIntro': 'Mi formación y mis certificaciones:',
  'about.cred1.name': 'NASM-CPT',
  'about.cred1.desc': 'Entrenador personal certificado',
  'about.cred2.name': 'NASM-CES',
  'about.cred2.desc': 'Especialista en ejercicio correctivo',
  'about.cred3.name': 'NASM-CNC',
  'about.cred3.desc': 'Coach de nutrición certificado',
  'about.cred4.name': 'Special Strong',
  'about.cred4.desc': 'Entrenador adaptado certificado',
  'about.cred5.name': 'Licenciatura en Kinesiología',
  'about.cred5.desc': 'Licenciatura en Kinesiología, Cal State San Marcos',
  'about.quote': '“El movimiento no es un privilegio. Es un derecho, y mi trabajo es hacerlo accesible para cada persona, sin importar su capacidad.”',
  'about.attr': 'James Kennedy, KinetiqAF',

  // Cómo funciona
  'process.label': 'Cómo funciona',
  'process.h2': 'De la primera llamada a la primera sesión',
  'process.s1.title': 'Una llamada gratuita de 20 minutos',
  'process.s1.body': 'Hablamos de sus metas y de su historial. Después de la llamada le envío un formulario de registro. Toma unos 15 minutos y se guarda mientras avanza.',
  'process.s2.title': 'Evaluación',
  'process.s2.body': 'Voy a donde usted está y veo cómo se mueve, para empezar en el punto correcto.',
  'process.s3.title': 'Su plan',
  'process.s3.body': 'Un programa hecho para sus metas, su espacio y su horario.',
  'process.s4.title': 'Entrenamiento',
  'process.s4.body': 'Sesiones en su casa o cerca de usted. El plan crece a la par de usted.',

  // Lo que las familias me preguntan primero — queue items CY y KV (E-09).
  'stories.label': 'Preocupaciones comunes',
  'stories.h2': 'Lo que las familias me preguntan primero',
  'stories.s1.title': 'Familias que ya probaron con otros entrenadores',
  'stories.s1.body': 'La mayoría de las familias de entrenamiento adaptado que llegan conmigo ya probaron con alguien más. Lo que faltó casi nunca fue conocimiento de acondicionamiento físico. Fue saber cómo cambiar la forma de comunicarse, cómo estructurar una sesión y cómo leer un día que no está saliendo bien. Esa es la parte para la que me preparé.',
  'stories.s2.title': 'Volver a empezar después de una cirugía',
  'stories.s2.body': 'Lo que más escucho después de un reemplazo articular o de una reparación es nerviosismo por siquiera empezar. Por eso arrancamos con una evaluación, vamos más despacio de lo que la mayoría espera y construimos alrededor de la vida a la que usted está regresando, no alrededor de una plantilla.',
  'stories.quotesLabel': 'En sus palabras',
  'stories.quotesH2': 'Lo que dicen los clientes',
  'stories.s3.title': 'El gimnasio era el obstáculo',
  'stories.s3.body': 'La gente me dice que el obstáculo nunca fue la motivación. Fue el traslado, la gente y el equipo. Entrenar en casa o en su vecindario quita la mayor parte de eso antes de empezar.',

  // Preguntas frecuentes
  'faq.label': 'Preguntas frecuentes',
  'faq.h2': 'Antes de que nos escriba',
  'faq.q1': '¿Necesito autorización médica después de una cirugía?',
  'faq.a1': 'Por lo general sí, y vale la pena conseguirla. Si viene de un reemplazo articular, de una reparación o de cualquier procedimiento con restricciones de actividad, le pido una autorización por escrito de su cirujano o de su médico antes de cargar cualquier ejercicio. A usted le cuesta un mensaje al consultorio, y a mí me dice exactamente qué movimientos están abiertos y cuáles no. Soy entrenador personal certificado, no un proveedor de salud con licencia. Esa autorización es lo que mantiene el entrenamiento del lado correcto de esa línea.',
  'faq.q2': '¿Cuánto cuesta?',
  'faq.a2': 'Hablamos de tarifas y paquetes en su llamada gratuita de 20 minutos, una vez que sepamos con qué frecuencia quiere entrenar. No hay presión para comprar un paquete. Las sesiones financiadas por el Centro Regional o el SDP no tienen costo de su bolsillo por los servicios cubiertos. Todo queda por escrito en un acuerdo sencillo que usted puede cancelar en cualquier momento.',
  'faq.q3': '¿Usted va a mi casa o a mi gimnasio?',
  'faq.a3': 'Lo que a usted le funcione mejor. La mayoría de las sesiones son a domicilio o en la comunidad, en todo el condado de San Diego: su sala, el gimnasio de su edificio, un parque cercano. Para quienes prefieren un gimnasio completo, trabajo en una instalación en Linda Vista. También hay sesiones virtuales para revisar su programa y para el coaching de nutrición. Las sesiones que paga el Centro Regional se realizan en espacios comunitarios, como parques y albercas, no en casa.',
  'faq.q4': '¿Mi familiar califica para el financiamiento del SDP?',
  'faq.a4': 'El Programa de Autodeterminación (SDP) está disponible para clientes de los Centros Regionales de California. Si su familiar recibe servicios de un Centro Regional (como el San Diego Regional Center), es posible que pueda usar financiamiento del SDP para contratar a los proveedores que elija, incluidos entrenadores de acondicionamiento adaptado como KinetiqAF. Comuníquese conmigo y le explico el proceso sin costo.',
  'faq.q5': '¿Con cuáles proveedores FMS trabaja?',
  'faq.a5': 'KinetiqAF trabaja con proveedores de Servicios de Administración Financiera (FMS) de toda la red de San Diego. Si ya tiene uno, dígame cuál. Confirmo que estamos dados de alta con ellos, o completo el papeleo que haga falta de mi lado. Usted solicita a KinetiqAF a través de su FMS, y yo me encargo del resto.',
  'faq.q6': '¿Ha trabajado con personas con discapacidad intelectual o del desarrollo?',
  'faq.a6': 'Tengo la designación Special Strong Certified Adaptive Trainer, una de las pocas credenciales diseñadas para profesionales del acondicionamiento que trabajan con personas con discapacidad intelectual y del desarrollo. Mi enfoque usa principios de Análisis Conductual Aplicado (ABA), estrategias de regulación sensorial y diseño de programas adaptados. No uso un protocolo igual para todos: cada cliente tiene su propio plan.',
  'faq.q7': '¿En cuánto tiempo veré resultados?',
  'faq.a7': 'Depende mucho de su punto de partida y de sus metas. En clientes que vienen de una cirugía, las mejoras funcionales significativas suelen aparecer entre 6 y 8 semanas de entrenamiento constante. En clientes adaptados, los avances conductuales y de habilidades motoras suelen verse antes, mientras que las medidas de condición física tardan más. Fijo expectativas realistas en la llamada gratuita y doy seguimiento a indicadores medibles desde la primera sesión.',
  'faq.q8': '¿Cuál es la política de cancelación?',
  'faq.a8': 'Se requiere aviso con 24 horas de anticipación para cancelar. Las cancelaciones tardías (con menos de 24 horas) y las ausencias se cobran como sesión. Entiendo que hay emergencias. Siempre las reviso con usted caso por caso, sobre todo con clientes adaptados, donde los retos de regulación inesperados son parte del terreno.',
  'faq.more': '¿Tiene una pregunta que no está aquí?',
  'faq.cta': 'Pida una llamada gratuita',

  // Contacto
  'contact.label': 'Comuníquese',
  'contact.h2': 'Pida una llamada gratuita de 20 minutos',
  'contact.body': 'Deje su nombre y su número de teléfono.',
  'contact.phone': '(442) 375-5090',
  'contact.phoneSub': 'Llamada o mensaje',
  'contact.email': 'james@kinetiqaf.com',
  'contact.emailSub': 'Correo electrónico',
  'contact.areaTitle': 'Dónde entreno',
  'contact.area': 'Todo el condado de San Diego: el centro de San Diego, la costa, North County, East County y South Bay.',
  'form.trap': 'Deje este campo en blanco',
  'form.name': 'Su nombre',
  'form.phone': 'Número de teléfono',
  'form.phoneHelp': 'Para devolverle la llamada',
  'form.email': 'Correo electrónico',
  'form.emailHelp': 'Para responderle por escrito',
  'form.useEmail': 'Usar correo en su lugar',
  'form.usePhone': 'Usar un teléfono en su lugar',
  'form.service': 'Qué le trae por aquí',
  'form.opt.none': 'Elija una opción',
  'form.opt.surgery': 'Después de una cirugía o lesión',
  'form.opt.strength': 'Ganar fuerza a cualquier edad',
  'form.opt.adaptive': 'Acondicionamiento adaptado para un familiar',
  'form.opt.exploring': 'Todavía no sé',
  'form.errName': 'Escriba su nombre',
  'form.errPhone': 'Escriba un número de teléfono, como 619 555 0123',
  'form.errEmail': 'Escriba un correo, como nombre@ejemplo.com',
  'form.errService': 'Elija qué le trae por aquí',
  'form.errSend': 'No se envió. Sus respuestas siguen aquí. Vuelva a intentarlo o llámeme.',
  'form.call': 'Llamar al (442) 375-5090',
  'form.retry': 'Intentar de nuevo',
  'form.submit': 'Pedir mi llamada gratuita',
  'form.sending': 'Enviando…',
  'form.sent': 'Enviado',
  'form.gotIt': 'Recibido, {name}.',
  'form.callBy': 'Le llamo a más tardar el {day}.',
  'form.writeBy': 'Le respondo a más tardar el {day}.',
  'form.miss': 'Si no le encuentro, le dejo un mensaje y le envío un texto.',

  // La barra de llamada en el teléfono
  'bar.aria': 'Comuníquese con James',
  'bar.call': 'Llamar',
  'bar.ask': 'Pida una llamada gratuita',

  // Pie de página
  'footer.copy': '© 2026 James Kennedy & Associates LLC · San Diego, CA',
  'footer.privacy': 'Política de privacidad',
  'footer.terms': 'Términos del servicio',
  'promise.reply': INQUIRY_REPLY.es,

  // La tarjeta de contacto (queue item IE). Las credenciales son nombres
  // propios de certificaciones y no se traducen — igual que en footer.creds.
  'card.doc.title': 'James Kennedy · KinetiqAF · Guarde mi contacto',
  'card.doc.description': 'Guarde los datos de contacto de James Kennedy, llámelo o envíele un mensaje, o pida una llamada gratis de 20 minutos. Entrenamiento personal a domicilio en San Diego.',
  'card.shareLine': 'Guarde mi contacto, llámeme o envíeme un mensaje, o pida una llamada gratis.',
  'card.role': 'Entrenador personal · Fundador de KinetiqAF',
  'card.creds': 'NASM CPT · CES · CNC · Certificado Special Strong',
  'card.what': 'Entrenamiento personal a domicilio en San Diego: después de una cirugía, fuerza a cualquier edad y ejercicio adaptado.',
  'card.save': 'Guardar contacto',
  'card.saveHint': 'Agrega a James a sus contactos.',
  'card.waysLabel': 'Formas de comunicarse con James',
  'card.row.call': 'Llamar',
  'card.row.text': 'Enviar un texto',
  'card.row.email': 'Correo',
  'card.row.ask': 'Pedir una llamada gratis',
  'card.row.askSub': '20 minutos. James le devuelve la llamada.',
  'card.row.website': 'Sitio web',
  'card.row.review': 'Deje una reseña en Google',
  'card.row.appIos': 'Obtenga la app para iPhone',
  'card.row.appAndroid': 'Obtenga la app para Android',
  'card.area': 'Zona de servicio',
  'card.areaSub': 'Todo el condado de San Diego · James va a donde usted esté',
  'card.markAlt': 'KinetiqAF',
  'card.footAria': 'Más',
  'card.lang': 'English',
  'card.langAria': 'Switch to English',
};

export const MARKETING_PACKS = Object.freeze({ en: EN, es: ES });

/** The pack for a language. Anything that is not Spanish is English — the rule
 *  intakePack and trainerApplyPack keep, for the same reason: English is the
 *  answer only when nothing else has said otherwise. */
export function marketingPack(lang) {
  return String(lang || '').toLowerCase().startsWith('es') ? ES : EN;
}

/**
 * Which language THIS visit reads, in priority order: `?lang=` on the URL, then
 * the choice this browser made last time on a PUBLIC page, then the browser's
 * own language.
 *
 * QUEUE ITEM BQ / R61 — the stored key is `kinetiqaf_public_lang`, which no
 * signed-in surface reads. A toggle on the marketing page must never change the
 * language of an account that later signs in on the same computer.
 *
 * Pure: the caller passes what it knows, so node can test it.
 */
export function marketingLangFrom({ search, stored, navigatorLang } = {}) {
  let param = null;
  try { param = new URLSearchParams(search || '').get('lang'); }
  catch (_) { param = null; /* malformed query — fall through */ }
  const first = [param, stored, navigatorLang].find((v) => v != null && String(v).trim() !== '');
  return String(first || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
}

/**
 * Paint one language onto a document.
 *
 * Text is written with textContent, never innerHTML, so a translation cannot
 * inject markup. Returns the pack it applied, so a caller can go on reading
 * strings from it — which is how the contact form's own error lines are worded.
 */
export function applyMarketingLang(doc, lang) {
  const pack = marketingPack(lang);
  const code = pack === ES ? 'es' : 'en';
  if (!doc || !doc.documentElement) return pack;
  doc.documentElement.setAttribute('lang', code);
  const set = (sel, attr, apply) => {
    doc.querySelectorAll(sel).forEach((el) => {
      const key = el.getAttribute(attr);
      const value = pack[key];
      // R17 — never a blank where a sentence belongs. A key with no value in
      // this pack leaves the markup's own text standing rather than erasing it.
      if (typeof value === 'string' && value !== '') apply(el, value);
    });
  };
  set('[data-i18n]', 'data-i18n', (el, v) => { el.textContent = v; });
  set('[data-i18n-placeholder]', 'data-i18n-placeholder', (el, v) => { el.setAttribute('placeholder', v); });
  set('[data-i18n-aria-label]', 'data-i18n-aria-label', (el, v) => { el.setAttribute('aria-label', v); });
  set('[data-i18n-title]', 'data-i18n-title', (el, v) => { el.setAttribute('title', v); });
  // Queue item GV — a photo slot's description is a sentence too.
  set('[data-i18n-alt]', 'data-i18n-alt', (el, v) => { el.setAttribute('alt', v); });
  // QUEUE ITEM IE — WHICH DOCUMENT'S TITLE.
  //
  // One pack now serves two documents: index.html and /card. `doc.title` is the
  // marketing home's, so painting it unconditionally gave the contact card the
  // landing page's <title> the moment a language was applied — a page that says
  // "In-Home Personal Training & Adaptive Fitness | San Diego, CA" in the tab
  // and "James Kennedy" on the screen, and a wrong name in every bookmark and
  // share sheet. The DOCUMENT names its own keys on <html>, defaulting to the
  // two this pack has always used, so index.html is byte-for-byte unaffected.
  const el = doc.documentElement;
  const titleKey = (el.getAttribute && el.getAttribute('data-i18n-title-key')) || 'doc.title';
  const descKey  = (el.getAttribute && el.getAttribute('data-i18n-desc-key'))  || 'doc.description';
  if (doc.title !== undefined && pack[titleKey]) doc.title = pack[titleKey];
  const desc = doc.querySelector && doc.querySelector('meta[name="description"]');
  if (desc && pack[descKey]) desc.setAttribute('content', pack[descKey]);
  // QUEUE ITEM KVb — the share lines, the locale and the canonical follow the
  // painted language too, in the words netlify/edge-functions/public-lang.js
  // writes for a crawler (both read marketingHead, so the two cannot drift).
  // Only the two documents this pack knows; any other page keeps its head.
  const page = { 'doc.title': 'home', 'card.doc.title': 'card' }[titleKey];
  if (page && doc.querySelector) paintMarketingHead(doc, marketingHead(page, code), code);
  return pack;
}

const CARD_SHARE_TITLE = 'James Kennedy · KinetiqAF';

/**
 * QUEUE ITEM KVb — the head words for one marketing document in one
 * language: `page` is 'home' (index.html) or 'card' (card.html). ONE source
 * for the browser's paint (applyMarketingLang, above) and the edge's rewrite
 * of `?lang=es` (netlify/edge-functions/public-lang.js), which is what a
 * crawler and a link preview read. Before KVb the edge had its own copy and
 * the browser painted only <title>, so a Spanish page said one title in the
 * tab and another in og:title (gv-public-site "(g) one title everywhere").
 * Pure: no DOM.
 */
export function marketingHead(page, lang) {
  const p = marketingPack(lang);
  if (page === 'card') {
    return {
      title: p['card.doc.title'], description: p['card.doc.description'],
      ogTitle: CARD_SHARE_TITLE, ogDescription: p['card.role'] + '. ' + p['card.what'],
      twitterTitle: CARD_SHARE_TITLE, twitterDescription: p['card.shareLine'],
    };
  }
  return {
    title: p['doc.title'], description: p['doc.description'],
    ogTitle: p['doc.title'], ogDescription: p['doc.description'],
    twitterTitle: p['doc.title'], twitterDescription: p['doc.description'],
  };
}

/** `https://kinetiqaf.com/card` ⇄ `https://kinetiqaf.com/card?lang=es`. Pure. */
export function marketingLangUrl(href, lang) {
  try {
    const u = new URL(String(href).replace(/&amp;/g, '&'));
    if (String(lang).toLowerCase().startsWith('es')) u.searchParams.set('lang', 'es');
    else u.searchParams.delete('lang');
    return u.toString();
  } catch (_) {
    return href;
  }
}

/** Write one head onto a live document: the browser half of the edge rewrite. */
function paintMarketingHead(doc, head, code) {
  const meta = (key, value) => {
    const el = doc.querySelector('meta[property="' + key + '"], meta[name="' + key + '"]');
    if (el && typeof value === 'string' && value !== '') el.setAttribute('content', value);
  };
  meta('og:title', head.ogTitle);
  meta('og:description', head.ogDescription);
  meta('twitter:title', head.twitterTitle);
  meta('twitter:description', head.twitterDescription);
  meta('og:locale', code === 'es' ? 'es_MX' : 'en_US');
  meta('og:locale:alternate', code === 'es' ? 'en_US' : 'es_MX');
  const canon = doc.querySelector('link[rel="canonical"]');
  if (canon) canon.setAttribute('href', marketingLangUrl(canon.getAttribute('href'), code));
  const ogUrl = doc.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.setAttribute('content', marketingLangUrl(ogUrl.getAttribute('content'), code));
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    MARKETING_PACKS, marketingPack, marketingLangFrom, applyMarketingLang,
  });
}
